import { Controller } from "@hotwired/stimulus";
import { marked } from 'marked';
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = [
    "messagesContainer",
    "messagesInput",
    "jsonPreview",
    "messageTemplate",
    "toolCallTemplate",
    "validationStatus",
    "validationErrors",
    "errorsList",
    "toolsReference"
  ]
  static values = {
    messages: Array,
    availableFunctions: Array
  }

  connect() {
    this.messages = this.messagesValue.length > 0 ? this.messagesValue : [
      { role: "user", content: "" }
    ];
    this.availableFunctions = this.availableFunctionsValue || [];

    this.renderMessages();
    this.updateJsonPreview();
    this.renderToolsReference();
    this.setupMainCollapseHandler();

    // Ensure all textareas are properly sized after initial render
    setTimeout(() => this.resizeAllTextareas(), 50);

    // Configure marked for security (similar to your Redcarpet config)
    marked.setOptions({
      breaks: true,
      gfm: true,
      sanitize: false, // We'll use DOMPurify or Rails' built-in sanitization
      smartLists: true,
      smartypants: false
    });
  }

  setupMainCollapseHandler() {
    const mainCollapseButton = document.querySelector('[data-bs-target="#availableToolsCollapse"]');
    const mainCollapseIcon = document.querySelector('.collapse-icon-main');

    if (mainCollapseButton && mainCollapseIcon) {
      // Set initial state (collapsed)
      mainCollapseIcon.style.transform = 'rotate(-90deg)';

      mainCollapseButton.addEventListener('click', () => {
        const targetCollapse = document.querySelector('#availableToolsCollapse');

        // Toggle icon rotation
        setTimeout(() => {
          if (targetCollapse.classList.contains('show')) {
            mainCollapseIcon.style.transform = 'rotate(0deg)';
          } else {
            mainCollapseIcon.style.transform = 'rotate(-90deg)';
          }
        }, 150);
      });
    }
  }

  resizeAllTextareas() {
    // Find and resize all message content textareas
    const messageTextareas = this.element.querySelectorAll('.message-content');
    messageTextareas.forEach(textarea => this.autoResize(textarea));

    // Find and resize all tool argument textareas
    const toolTextareas = this.element.querySelectorAll('.tool-arguments');
    toolTextareas.forEach(textarea => this.autoResize(textarea));
  }

  autoResize(textarea) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Set the height to the scrollHeight, with min and max constraints
    const minHeight = 24; // minimum height in pixels (single line)
    const maxHeight = textarea.classList.contains('tool-arguments') ? 200 : 300; // different max for different types
    const scrollHeight = Math.max(minHeight, Math.min(maxHeight, textarea.scrollHeight));

    textarea.style.height = scrollHeight + 'px';
  }

  setupTextareaAutoResize(textarea) {
    // Set initial height with a small delay to ensure DOM is ready
    setTimeout(() => this.autoResize(textarea), 0);

    // Add event listeners for auto-resize
    textarea.addEventListener('input', () => this.autoResize(textarea));
    textarea.addEventListener('paste', () => {
      // Small delay to allow paste content to be processed
      setTimeout(() => this.autoResize(textarea), 10);
    });
  }

  addMessage() {
    this.messages.push({ role: "user", content: "" });
    this.renderMessages();
    this.updateJsonPreview();

    // Scroll to the new message
    setTimeout(() => {
      const newMessage = this.messagesContainerTarget.lastElementChild;
      if (newMessage) {
        newMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus on content textarea
        const contentTextarea = newMessage.querySelector('.message-content');
        if (contentTextarea) {
          contentTextarea.focus();
        }
      }
    }, 100);
  }

  removeMessage(event) {
    const messageItem = event.target.closest('.message-item');
    const index = parseInt(messageItem.dataset.messageIndex);

    if (this.messages.length > 1) {
      this.messages.splice(index, 1);
      this.renderMessages();
      this.updateJsonPreview();
    } else {
      // Show a brief message that at least one message is required
      this.showTempMessage('At least one message is required');
    }
  }

  moveMessageUp(event) {
    const messageItem = event.target.closest('.message-item');
    const index = parseInt(messageItem.dataset.messageIndex);

    if (index > 0) {
      // Swap with previous message
      [this.messages[index - 1], this.messages[index]] = [this.messages[index], this.messages[index - 1]];
      this.renderMessages();
      this.updateJsonPreview();

      // Scroll to the moved message
      setTimeout(() => {
        const movedMessage = this.messagesContainerTarget.children[index - 1];
        if (movedMessage) {
          movedMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  moveMessageDown(event) {
    const messageItem = event.target.closest('.message-item');
    const index = parseInt(messageItem.dataset.messageIndex);

    if (index < this.messages.length - 1) {
      // Swap with next message
      [this.messages[index], this.messages[index + 1]] = [this.messages[index + 1], this.messages[index]];
      this.renderMessages();
      this.updateJsonPreview();

      // Scroll to the moved message
      setTimeout(() => {
        const movedMessage = this.messagesContainerTarget.children[index + 1];
        if (movedMessage) {
          movedMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  updateMessages(event) {
    const messageItem = event.target.closest('.message-item');
    const index = parseInt(messageItem.dataset.messageIndex);
    const role = messageItem.querySelector('.message-role').value;
    const contentTextarea = messageItem.querySelector('.message-content');
    const content = contentTextarea.value;

    // Auto-resize textarea if it's the content field
    if (event.target.classList.contains('message-content')) {
      this.autoResize(event.target);
    }

    // Auto-resize tool argument textareas
    if (event.target.classList.contains('tool-arguments')) {
      this.autoResize(event.target);
    }

    // Update the message object
    this.messages[index] = { ...this.messages[index], role, content };

    // Handle tool_call_id for tool messages
    if (role === 'tool') {
      const toolCallIdInput = messageItem.querySelector('.tool-call-id');
      if (toolCallIdInput) {
        const toolCallId = toolCallIdInput.value;
        if (toolCallId.trim()) {
          this.messages[index].tool_call_id = toolCallId.trim();
        } else {
          delete this.messages[index].tool_call_id;
        }
      }
    } else {
      // Remove tool_call_id if not a tool message
      delete this.messages[index].tool_call_id;
    }

    // Update the data attribute for styling
    messageItem.dataset.role = role;

    // Show/hide sections based on role
    const toolCallsSection = messageItem.querySelector('.tool-calls-section');
    const toolCallIdSection = messageItem.querySelector('.tool-call-id-section');

    if (role === 'assistant') {
      toolCallsSection.classList.remove('d-none');
      toolCallIdSection.classList.add('d-none');
      // Show content section initially for assistant messages without tool calls
      this.updateContentVisibilityForAssistant(messageItem, index);
    } else if (role === 'tool') {
      toolCallsSection.classList.add('d-none');
      toolCallIdSection.classList.remove('d-none');
      contentTextarea.style.display = ''; // Show content for tool messages
      // Populate the tool call ID dropdown
      this.populateToolCallIdDropdown(messageItem);
    } else {
      toolCallsSection.classList.add('d-none');
      toolCallIdSection.classList.add('d-none');
      contentTextarea.style.display = ''; // Show content for user/system messages
      // Remove any existing tool calls when switching away from assistant
      if (this.messages[index].tool_calls) {
        delete this.messages[index].tool_calls;
      }
    }

    // Update tool calls if they exist
    this.updateToolCalls(messageItem, index);

    // Update all tool call ID dropdowns whenever messages change
    this.updateAllToolCallIdDropdowns();

    this.updateJsonPreview();

    // Render markdown preview for assistant messages
    if (event.target.classList.contains('message-content')) {
      this.renderMarkdownPreview(messageItem);
    }
  }

  updateContentVisibilityForAssistant(messageItem, index) {
    const contentTextarea = messageItem.querySelector('.message-content');
    const hasToolCalls = this.messages[index].tool_calls && this.messages[index].tool_calls.length > 0;

    if (hasToolCalls) {
      // Hide just the content textarea when there are tool calls
      contentTextarea.style.display = 'none';
      // Clear content since it shouldn't exist with tool calls
      this.messages[index].content = null;
      contentTextarea.value = '';
    } else {
      // Show content textarea when there are no tool calls
      contentTextarea.style.display = '';
    }
  }

  addToolCall(event) {
    const messageItem = event.target.closest('.message-item');
    const index = parseInt(messageItem.dataset.messageIndex);
    const toolCallsContainer = messageItem.querySelector('.tool-calls-container');

    // Initialize tool_calls array if it doesn't exist
    if (!this.messages[index].tool_calls) {
      this.messages[index].tool_calls = [];
    }

    // Generate a sequential call ID
    const callIndex = this.messages[index].tool_calls.length + 1;
    const defaultId = `call_${callIndex}`;

    // Add new tool call
    this.messages[index].tool_calls.push({
      id: defaultId,
      type: "function",
      function: {
        name: "",
        arguments: {}
      }
    });

    this.renderToolCallsForMessage(messageItem, this.messages[index].tool_calls);
    this.updateContentVisibilityForAssistant(messageItem, index);
    this.updateAllToolCallIdDropdowns();
    this.updateJsonPreview();
  }

  removeToolCall(event) {
    const messageItem = event.target.closest('.message-item');
    const toolCallItem = event.target.closest('.tool-call-item');
    const messageIndex = parseInt(messageItem.dataset.messageIndex);
    const toolCallIndex = Array.from(toolCallItem.parentNode.children).indexOf(toolCallItem);

    if (this.messages[messageIndex].tool_calls) {
      this.messages[messageIndex].tool_calls.splice(toolCallIndex, 1);

      // Remove tool_calls array if it's empty
      if (this.messages[messageIndex].tool_calls.length === 0) {
        delete this.messages[messageIndex].tool_calls;
      }

      this.renderToolCallsForMessage(messageItem, this.messages[messageIndex].tool_calls || []);
      this.updateContentVisibilityForAssistant(messageItem, messageIndex);
      this.updateAllToolCallIdDropdowns();
      this.updateJsonPreview();
    }
  }

  updateToolCalls(messageItem, messageIndex) {
    const toolCallItems = messageItem.querySelectorAll('.tool-call-item');

    if (toolCallItems.length > 0 && this.messages[messageIndex].role === 'assistant') {
      const toolCalls = [];

      toolCallItems.forEach((item, index) => {
        const callId = item.querySelector('.tool-call-id-input').value || `call_${messageIndex}_${index}`;
        const functionName = item.querySelector('.tool-function-name').value;
        const argumentsText = item.querySelector('.tool-arguments').value;

        let parsedArguments = {};
        try {
          if (argumentsText.trim()) {
            parsedArguments = JSON.parse(argumentsText);
          }
        } catch (e) {
          // Keep as string if invalid JSON
          parsedArguments = argumentsText;
        }

        toolCalls.push({
          id: callId,
          type: "function",
          function: {
            name: functionName,
            arguments: parsedArguments
          }
        });
      });

      if (toolCalls.length > 0) {
        this.messages[messageIndex].tool_calls = toolCalls;
      } else {
        delete this.messages[messageIndex].tool_calls;
      }
    }

    // Update content visibility after tool calls change
    if (this.messages[messageIndex].role === 'assistant') {
      this.updateContentVisibilityForAssistant(messageItem, messageIndex);
    }
  }

  renderMessages() {
    this.messagesContainerTarget.innerHTML = '';

    this.messages.forEach((message, index) => {
      const template = this.messageTemplateTarget.content.cloneNode(true);
      const messageItem = template.querySelector('.message-item');

      messageItem.dataset.messageIndex = index;
      messageItem.dataset.role = message.role || 'user';

      const roleSelect = template.querySelector('.message-role');
      const contentTextarea = template.querySelector('.message-content');
      const moveUpBtn = template.querySelector('.move-up-btn');
      const moveDownBtn = template.querySelector('.move-down-btn');

      roleSelect.value = message.role || 'user';
      contentTextarea.value = message.content || '';

      // Disable move buttons appropriately
      moveUpBtn.disabled = (index === 0);
      moveDownBtn.disabled = (index === this.messages.length - 1);

      // Show appropriate sections based on role
      if (message.role === 'assistant') {
        const toolCallsSection = template.querySelector('.tool-calls-section');
        toolCallsSection.classList.remove('d-none');

        // Render existing tool calls if any
        if (message.tool_calls) {
          this.renderToolCallsForMessage(messageItem, message.tool_calls);
        }

        // Update content visibility for assistant messages
        this.updateContentVisibilityForAssistant(messageItem, index);
      } else if (message.role === 'tool') {
        const toolCallIdSection = template.querySelector('.tool-call-id-section');
        toolCallIdSection.classList.remove('d-none');
      }

      this.messagesContainerTarget.appendChild(template);

      // Setup auto-resize for the content textarea after it's in the DOM
      this.setupTextareaAutoResize(contentTextarea);
    });

    // Populate all tool call ID dropdowns after all messages are rendered
    setTimeout(() => {
      this.updateAllToolCallIdDropdowns();
      this.setExistingToolCallIds();
    }, 0);
  }

  // Set existing tool_call_id values after dropdowns are populated
  setExistingToolCallIds() {
    this.messages.forEach((message, index) => {
      if (message.role === 'tool' && message.tool_call_id) {
        const messageItem = this.messagesContainerTarget.children[index];
        const dropdown = messageItem.querySelector('.tool-call-id');
        if (dropdown) {
          dropdown.value = message.tool_call_id;
        }
      }
    });
  }

  renderToolCallsForMessage(messageItem, toolCalls) {
    const container = messageItem.querySelector('.tool-calls-container');
    container.innerHTML = '';

    if (!toolCalls || toolCalls.length === 0) {
      return;
    }

    toolCalls.forEach((toolCall) => {
      const template = this.toolCallTemplateTarget.content.cloneNode(true);
      const callIdInput = template.querySelector('.tool-call-id-input');
      const functionNameSelect = template.querySelector('.tool-function-name-select');
      const functionNameInput = template.querySelector('.tool-function-name');
      const argumentsTextarea = template.querySelector('.tool-arguments');

      callIdInput.value = toolCall.id || '';

      // Populate function name dropdown
      this.populateFunctionNameDropdown(functionNameSelect, toolCall.function?.name);
      functionNameInput.value = toolCall.function?.name || '';

      // Format arguments as JSON
      let argumentsText = '';
      if (toolCall.function?.arguments) {
        try {
          argumentsText = typeof toolCall.function.arguments === 'string'
            ? toolCall.function.arguments
            : JSON.stringify(toolCall.function.arguments, null, 2);
        } catch (e) {
          argumentsText = toolCall.function.arguments.toString();
        }
      }
      argumentsTextarea.value = argumentsText;

      // Update parameter hints
      this.updateParameterHints(template, toolCall.function?.name);

      container.appendChild(template);

      // Setup auto-resize for the arguments textarea after it's in the DOM
      this.setupTextareaAutoResize(argumentsTextarea);
    });
  }

  populateFunctionNameDropdown(selectElement, selectedValue = '') {
    // Clear existing options except the first one
    selectElement.innerHTML = '<option value="">Select a function...</option>';

    this.availableFunctions.forEach(func => {
      const option = document.createElement('option');
      option.value = func.value;
      option.textContent = func.label;
      option.title = func.description;
      if (func.value === selectedValue) {
        option.selected = true;
      }
      selectElement.appendChild(option);
    });
  }

  onFunctionNameChange(event) {
    const selectElement = event.target;
    const toolCallItem = selectElement.closest('.tool-call-item');
    const hiddenInput = toolCallItem.querySelector('.tool-function-name');
    const selectedValue = selectElement.value;

    // Update the hidden input
    hiddenInput.value = selectedValue;

    // Update parameter hints
    this.updateParameterHints(toolCallItem, selectedValue);

    // Trigger messages update
    this.updateMessages(event);
  }

  toggleFunctionInputMode(event) {
    const toolCallItem = event.target.closest('.tool-call-item');
    const selectElement = toolCallItem.querySelector('.tool-function-name-select');
    const inputElement = toolCallItem.querySelector('.tool-function-name');
    const toggleButton = event.target.closest('.toggle-input-mode');

    if (selectElement.classList.contains('d-none')) {
      // Show dropdown, hide input
      selectElement.classList.remove('d-none');
      inputElement.classList.add('d-none');
      toggleButton.innerHTML = '<i class="fas fa-edit"></i>';
      toggleButton.title = 'Switch to text input';
    } else {
      // Show input, hide dropdown
      selectElement.classList.add('d-none');
      inputElement.classList.remove('d-none');
      inputElement.focus();
      toggleButton.innerHTML = '<i class="fas fa-list"></i>';
      toggleButton.title = 'Switch to dropdown';
    }
  }

  updateParameterHints(toolCallItem, functionName) {
    const hintsContainer = toolCallItem.querySelector('.function-params-hint');

    if (!hintsContainer) {
      // If hints container doesn't exist, just return without doing anything
      return;
    }

    if (!functionName) {
      hintsContainer.classList.add('d-none');
      return;
    }

    const functionInfo = this.availableFunctions.find(f => f.value === functionName);
    if (!functionInfo || !functionInfo.parameters || !functionInfo.parameters.properties) {
      hintsContainer.classList.add('d-none');
      return;
    }

    const params = functionInfo.parameters.properties;
    const required = functionInfo.parameters.required || [];

    let hintsHtml = '<strong>Parameters:</strong><br>';
    Object.entries(params).forEach(([paramName, paramInfo]) => {
      const isRequired = required.includes(paramName);
      const requiredBadge = isRequired ? `<span class="${badgeClasses({ variant: 'danger', size: 'sm' })}">required</span>` : '';
      hintsHtml += `<code>${paramName}</code> (${paramInfo.type}) ${requiredBadge}`;
      if (paramInfo.description) {
        hintsHtml += ` - ${paramInfo.description}`;
      }
      hintsHtml += '<br>';
    });

    hintsContainer.innerHTML = hintsHtml;
    hintsContainer.classList.remove('d-none');
  }

  renderToolsReference() {
    // Only handle the collapse icon setup since HTML is now rendered in ERB
    if (!this.hasToolsReferenceTarget) {
      return;
    }

    const container = this.toolsReferenceTarget;

    // Add event listeners for collapse icons
    container.querySelectorAll('[data-bs-toggle="collapse"]').forEach(button => {
      button.addEventListener('click', (e) => {
        const icon = button.querySelector('.collapse-icon');
        const target = button.getAttribute('data-bs-target');
        const collapse = container.querySelector(target);

        // Toggle icon rotation
        setTimeout(() => {
          if (collapse.classList.contains('show')) {
            icon.style.transform = 'rotate(0deg)';
          } else {
            icon.style.transform = 'rotate(-90deg)';
          }
        }, 150);
      });
    });

    // Set all icons to collapsed state initially
    container.querySelectorAll('.collapse-icon').forEach((icon) => {
      icon.style.transform = 'rotate(-90deg)';
    });
  }

  formatToolName(className) {
    // Extract just the class name without the module path
    // "Ai::Tools::AdminModelManager" -> "AdminModelManager"
    return className.split('::').pop();
  }

  updateJsonPreview() {
    const jsonString = JSON.stringify(this.messages, null, 2);
    this.jsonPreviewTarget.textContent = jsonString;
    this.messagesInputTarget.value = jsonString;
    this.validateMessages();
  }

  validateMessages() {
    const errors = this.collectValidationErrors();
    this.displayValidationResults(errors);
  }

  collectValidationErrors() {
    const errors = [];

    // Basic array validation
    if (!Array.isArray(this.messages) || this.messages.length === 0) {
      errors.push('Messages must be a non-empty array');
      return errors;
    }

    // Validate each message
    this.messages.forEach((message, index) => {
      const messageErrors = this.validateMessage(message, index);
      errors.push(...messageErrors);
    });

    return errors;
  }

  validateMessage(message, index) {
    const errors = [];

    // Check if message is an object
    if (!message || typeof message !== 'object') {
      errors.push(`Message ${index + 1}: must be an object`);
      return errors;
    }

    // Check for role field
    if (!message.role) {
      errors.push(`Message ${index + 1}: missing 'role' field`);
      return errors;
    }

    // Check valid roles
    const validRoles = ['system', 'user', 'assistant', 'tool'];
    if (!validRoles.includes(message.role)) {
      errors.push(`Message ${index + 1}: invalid role '${message.role}'. Must be one of: ${validRoles.join(', ')}`);
      return errors;
    }

    // Role-specific validation
    switch (message.role) {
      case 'system':
      case 'user':
        if (!message.content || message.content.trim() === '') {
          errors.push(`Message ${index + 1}: ${message.role} messages must have 'content'`);
        }
        break;
      case 'assistant':
        errors.push(...this.validateAssistantMessage(message, index));
        break;
      case 'tool':
        errors.push(...this.validateToolMessage(message, index));
        break;
    }

    return errors;
  }

  validateAssistantMessage(message, index) {
    const errors = [];
    const hasContent = message.content && message.content.trim() !== '';
    const hasToolCalls = Array.isArray(message.tool_calls) && message.tool_calls.length > 0;

    if (!hasContent && !hasToolCalls) {
      errors.push(`Message ${index + 1}: assistant messages must have either 'content' or 'tool_calls'`);
    }

    // Assistant messages should not have both content and tool_calls
    if (hasContent && hasToolCalls) {
      errors.push(`Message ${index + 1}: assistant messages cannot have both 'content' and 'tool_calls'`);
    }

    if (hasToolCalls) {
      message.tool_calls.forEach((toolCall, toolIndex) => {
        errors.push(...this.validateToolCall(toolCall, index, toolIndex));
      });
    }

    return errors;
  }

  validateToolMessage(message, index) {
    const errors = [];

    if (!message.content || message.content.trim() === '') {
      errors.push(`Message ${index + 1}: tool messages must have 'content'`);
    }

    if (!message.tool_call_id || message.tool_call_id.trim() === '') {
      errors.push(`Message ${index + 1}: tool messages must have 'tool_call_id'`);
    }

    return errors;
  }

  validateToolCall(toolCall, messageIndex, toolIndex) {
    const errors = [];

    if (!toolCall || typeof toolCall !== 'object') {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: must be an object`);
      return errors;
    }

    if (!toolCall.id || toolCall.id.trim() === '') {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: missing 'id' field`);
    }

    if (toolCall.type !== 'function') {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: type must be 'function'`);
    }

    if (!toolCall.function || typeof toolCall.function !== 'object') {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: 'function' must be an object`);
      return errors;
    }

    if (!toolCall.function.name || toolCall.function.name.trim() === '') {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: function missing 'name' field`);
    }

    if (toolCall.function.arguments === undefined || toolCall.function.arguments === null) {
      errors.push(`Message ${messageIndex + 1}, tool call ${toolIndex + 1}: function missing 'arguments' field`);
    }

    return errors;
  }

  displayValidationResults(errors) {
    const statusTarget = this.validationStatusTarget;
    const errorsTarget = this.validationErrorsTarget;
    const errorsListTarget = this.errorsListTarget;

    if (errors.length === 0) {
      // Valid
      statusTarget.innerHTML = `
        <span class="${badgeClasses({ variant: 'success' })}">
          <i class="fas fa-check me-1"></i>Valid
        </span>
      `;
      errorsTarget.classList.add('d-none');
    } else {
      // Invalid
      statusTarget.innerHTML = `
        <span class="${badgeClasses({ variant: 'danger' })}">
          <i class="fas fa-exclamation-triangle me-1"></i>Invalid (${errors.length} error${errors.length > 1 ? 's' : ''})
        </span>
      `;

      // Show errors
      errorsListTarget.innerHTML = errors.map(error => `<li>${error}</li>`).join('');
      errorsTarget.classList.remove('d-none');
    }
  }

  // Collect all tool call IDs from assistant messages
  collectAvailableToolCallIds() {
    const toolCallIds = [];

    this.messages.forEach((message) => {
      if (message.role === 'assistant' && message.tool_calls) {
        message.tool_calls.forEach((toolCall) => {
          if (toolCall.id && toolCall.id.trim()) {
            toolCallIds.push(toolCall.id.trim());
          }
        });
      }
    });

    return [...new Set(toolCallIds)]; // Remove duplicates
  }

  // Populate a specific tool call ID dropdown
  populateToolCallIdDropdown(messageItem) {
    const dropdown = messageItem.querySelector('.tool-call-id');
    if (!dropdown) return;

    const availableIds = this.collectAvailableToolCallIds();
    const currentValue = dropdown.value;

    // Clear existing options except the first one
    dropdown.innerHTML = '<option value="">Select a tool call ID...</option>';

    if (availableIds.length === 0) {
      dropdown.innerHTML = '<option value="">No tool calls available</option>';
      dropdown.disabled = true;
    } else {
      dropdown.disabled = false;

      availableIds.forEach(id => {
        const option = document.createElement('option');
        option.value = id;
        option.textContent = id;
        if (id === currentValue) {
          option.selected = true;
        }
        dropdown.appendChild(option);
      });
    }
  }

  // Update all tool call ID dropdowns
  updateAllToolCallIdDropdowns() {
    const toolMessages = this.messagesContainerTarget.querySelectorAll('.message-item[data-role="tool"]');
    toolMessages.forEach(messageItem => {
      this.populateToolCallIdDropdown(messageItem);
    });
  }

  showTempMessage(text) {
    // Create a temporary toast-like message
    const message = document.createElement('div');
    message.className = 'alert alert-warning alert-dismissible fade show position-fixed';
    message.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
    message.innerHTML = `
      <small>${text}</small>
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;

    document.body.appendChild(message);

    // Auto-remove after 3 seconds
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message);
      }
    }, 3000);
  }

  // Add new method for rendering markdown
  renderMarkdownPreview(messageItem) {
    const role = messageItem.dataset.role;
    if (role !== 'assistant') return;

    const textarea = messageItem.querySelector('.message-content');
    const previewContainer = messageItem.querySelector('.markdown-preview');

    if (!textarea || !previewContainer) return;

    const markdownText = textarea.value;
    if (markdownText.trim()) {
      const htmlContent = marked.parse(markdownText);
      previewContainer.innerHTML = htmlContent;
    } else {
      previewContainer.innerHTML = '<em class="text-muted">Preview will appear here...</em>';
    }
  }

  // Add toggle functionality
  toggleMarkdownPreview(event) {
    const messageItem = event.target.closest('.message-item');
    const textarea = messageItem.querySelector('.message-content');
    const preview = messageItem.querySelector('.markdown-preview');
    const toggleBtn = messageItem.querySelector('.markdown-toggle');

    if (textarea.classList.contains('d-none')) {
      // Show textarea, hide preview
      textarea.classList.remove('d-none');
      preview.classList.add('d-none');
      toggleBtn.innerHTML = '<i class="fas fa-eye me-1"></i>Preview';
    } else {
      // Show preview, hide textarea
      textarea.classList.add('d-none');
      preview.classList.remove('d-none');
      toggleBtn.innerHTML = '<i class="fas fa-edit me-1"></i>Edit';
      this.renderMarkdownPreview(messageItem);
    }
  }
}
